import { Alert, AlertTitle, alpha, Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { AutogiroMandateSentEnum } from 'src/shared/models/enums/autogiroMandateSent.enum';
import { AutogiroMandateStatus } from 'src/modules/invoice/components/autogiroMandateStatus';
import { AutogiroMandateResponse } from 'src/modules/invoice/models/autogiroMandateResponse';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useState } from 'react';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { AutogiroMandateModal } from 'src/modules/invoice/components/autogiroMandateModal';
import { deleteAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { Member } from '../models/member';

type AutogiroMandateInfoSectionProps = {
  member: Member;
  refetchMemberCallback: () => void;
};

export const AutogiroMandateInfoSection = (props: AutogiroMandateInfoSectionProps) => {
  const { member, refetchMemberCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [mandateModalOpen, setMandateModalOpen] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  // Ett godkänt eller skickat medgivande finns hos Bankgirot och ska makuleras, inte raderas.
  const canDeleteMandate =
    member.autogiroMandate.sent === AutogiroMandateSentEnum.NotSent &&
    (member.autogiroMandate.status === AutogiroMandateStatusEnum.New ||
      member.autogiroMandate.status === AutogiroMandateStatusEnum.NotApproved ||
      member.autogiroMandate.status === AutogiroMandateStatusEnum.Error);
  const [, setLoading] = useState<boolean>(false);

  const mandateResponse: AutogiroMandateResponse = {
    ...member.autogiroMandate,
    bankIdIpAddress: '',
    bankIdSignedFileName: '',
    bankIdUserNonVisibleData: '',
    bankIdXMLSignature: '',
    autogiroMandateLog: [],
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteAutogiroMandate(member.autogiroMandate.autogiroMandate_id);
      if (isMounted()) {
        toast.success(<>{t('member:autogiroMandateInfoSection.deleteMandateSuccess')}</>);
        refetchMemberCallback();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('member:autogiroMandateInfoSection.deleteMandateError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <Grid container pl={1} mt={1}>
        <Grid item xs={12} mt={1} sx={{ backgroundColor: (thm) => alpha(thm.palette.grey[200], 0.35), borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {t('member:autogiroMandateInfoSection.mandate')}
          </Typography>

          <Box display='flex' justifyContent='space-between'>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='body2' mt='1.5px'>
                {member.autogiroMandate.payerNr}
              </Typography>
              <AutogiroMandateStatus item={mandateResponse} />
            </Box>

            <Stack direction='row' spacing={2} alignItems='center'>
              <Box>
                <Button
                  variant='text'
                  size='small'
                  sx={{
                    textTransform: 'none',
                    px: 0,
                    minWidth: 0,
                    marginBottom: -1,
                  }}
                  onClick={() => setMandateModalOpen(true)}
                >
                  {t('common:button.open')}
                </Button>
                <Divider />
              </Box>

              {canDeleteMandate && (
                <Box>
                  <Button
                    variant='text'
                    size='small'
                    sx={{
                      textTransform: 'none',
                      px: 0,
                      minWidth: 0,
                      marginBottom: -1,
                    }}
                    onClick={() => setOpenDeleteDialog(true)}
                  >
                    {t('common:button.delete')}
                  </Button>
                  <Divider />
                </Box>
              )}
            </Stack>
          </Box>
        </Grid>

        {member.autogiroStatus && member.autogiroMandate.status !== AutogiroMandateStatusEnum.Approved && (
          <Grid item xs={12} mt={1}>
            <Alert severity='error' icon={false}>
              <AlertTitle sx={{ fontWeight: 600 }}>{t('member:autogiroMandateInfoSection.noMandate')}</AlertTitle>
              {t('member:autogiroMandateInfoSection.noMandateDescription', { payerNr: member.autogiroMandate.payerNr || '' })}
            </Alert>
          </Grid>
        )}
      </Grid>

      {mandateModalOpen && (
        <AutogiroMandateModal
          autogiroMandateId={member.autogiroMandate.autogiroMandate_id}
          onClose={() => setMandateModalOpen(false)}
          onSave={() => {
            setMandateModalOpen(false);
          }}
          modalOpen={mandateModalOpen}
        />
      )}
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('member:autogiroMandateInfoSection.deleteMandate')}
        onClose={(value) => {
          if (value) {
            handleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
